/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi } from 'final-form';
import { IApiResponse } from 'utils/models';
import debounce from 'utils/debounce';
import api from 'api/api';
import auth from 'utils/auth';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import BulksWrapper from 'components/modules/Beneficiaries/BulksModules/BulksWrapper/BulksWrapper';
import Form from 'ui/Form/Form';
import Input from 'ui/Input/Input';
import { AccountInput, Select, SelectItem } from 'ui/Input';
import { AxiosStatus } from 'api/axiosConfig';
import noop from 'utils/noop';
import { requiredOnly } from 'utils/validation';
import Button from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import { StatusModal, WarningModal } from 'components/modals';

import { IApiBranchReponse, IBranch, IInstantBank, IBank } from 'components/modules/Beneficiaries/Beneficiaries.model';
import { IBeneficiaryTransfer, ITransformedTransferBulkId } from './IBenBulkTransfer.model';

import styles from './EditBenBulkTransfer.module.scss';

import plus from 'assets/icons/plus.svg';
import trash from 'assets/icons/trash.png';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

export interface IFormValues {
  beneficiaryData: {
    beneficiaryId?: number,
    branchList: IBranch[],
    fullName: string,
    transferType?: string,
    bankCode: string,
    bankBranchCode: string,
    accountNumber: string,
    isTransferTypeDisabled?: boolean,
  }[];
}

// const schema = (values: IFormValues) => yup.object().shape({
//   beneficiaryData: yup.array().of(
//     yup.object().shape({
//       name: requiredOnly('Full Name'),
//       transferType: requiredOnly('Transfer Type'),
//       bankCode: requiredOnly('Bank Name'),
//       bankBranchCode: requiredOnly('Branch Name'),
//       accountNumber: requiredOnly('Account Number'),
//     }),
//   ),
// });

const schema = yup.object().shape({
  beneficiaryData: yup.array().of(
    yup.object().shape({
      fullName: requiredOnly('Full Name'),
      transferType: requiredOnly('Transfer Type'),
      // bankCode: requiredOnly('Bank Name'),
      bankCode: yup.string().when('transferType', {
        is: (transferType: string) => {
          if (transferType === 'INSTANT_INTERBANK' || transferType === 'NON_INSTANT_INTERBANK') {
            return true;
          }
          return false;
        },
        then: requiredOnly('Bank Name'),
        otherwise: yup.string(),
      }),
      bankBranchCode: yup.string().when('transferType', {
        is: 'NON_INSTANT_INTERBANK',
        then: requiredOnly('Branch Name'),
        otherwise: yup.string(),
      }),
      accountNumber: requiredOnly('Account Number'),
    }),
  ),
});

const validate = (values: IFormValues) => {
  try {
    schema.validateSync(values, { abortEarly: false });
  } catch (e) {
    const valErrs = mapYupObjectError<IFormValues>(e);
    return objectMap(valErrs, (v) => v[0]);
  }
  return undefined;
};

interface IEditPrepaidBulkBen {
  data: ITransformedTransferBulkId;
  instantBanks: IInstantBank[];
  nonInstantBanks: IBank[];
  onClose: () => void;
}

const EditTransferBulkBen = ({ data, onClose, instantBanks, nonInstantBanks }: IEditPrepaidBulkBen) => {
  const [dataState, setDataState] = useState<IBeneficiaryTransfer[] | null>(null);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const [showWarning, setShowWarning] = useState(false);
  const [submitWarningStatus, setSubmitWarningStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const warningSubmitting = submitWarningStatus === AxiosStatus.PENDING;
  const [showStatus, setShowStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitWarningStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const [loadingBranchList, setLoadingBranchList] = useState(false);

  const [loadingInitialBranches, setLoadingInitialBranches] = useState(false);

  const [isLookingUp, setLookingUp] = useState(false);
  const [lookingIndex, setLookingIndex] = useState<number | null>(null);
  const [lookingErrorText, setLookingErrorText] = useState<Record<string, string>>({});
  const updateLookingErrorText = (update: Record<string, string>) =>
    setLookingErrorText((current) => ({ ...current, ...update }));
  const removeKeyFromLookingErrorText = (keyToRemove: string) => {
    setLookingErrorText((current) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [keyToRemove]: removedKey, ...rest } = current;
      return rest;
    });
  };

  let mainForm!: FormType;

  useEffect(() => {
    const fetchBankBranches = (id: string) => api
      .get(`/transfer/bankBranch?bankSortCode=${id}`, {}, true, true)
      .then((response: IApiBranchReponse) => {
        console.log(response);
        return response.banks;
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        return [];
      });

    setLoadingInitialBranches(true);

    Promise.all(data.beneficiaries.map((beneficiary) => fetchBankBranches(beneficiary.bankCode)))
      .then((branchesList) => {
        // console.log(branchesList);
        const mergedBranches = data.beneficiaries.map((beneficiary, index) => ({ ...beneficiary, branchList: branchesList[index] }));
        setDataState(mergedBranches);
      })
      .catch((error) => console.error(error))
      .finally(() => setLoadingInitialBranches(false));
  }, []);

  const initialValues = useMemo(() => {
    const beneficiaryDataArray = dataState?.map((beneficiary) => {
      const ben: any = {
        beneficiaryId: beneficiary.id,
        branchList: beneficiary.branchList,
        fullName: beneficiary.name,
        transferType: beneficiary.transferType,
        accountNumber: beneficiary.accountNumber,
        isTransferTypeDisabled: true,
      };

      if (beneficiary.bankCode !== null) {
        ben.bankCode = beneficiary.bankCode;
      }

      if (beneficiary.branchCode !== null) {
        ben.bankBranchCode = beneficiary.branchCode;
      }

      return ben;
    });

    const iniitalData = {
      beneficiaryData: beneficiaryDataArray,
    };

    return iniitalData;
  }, [dataState]);

  const lookupAccount = (values: IFormValues, accountNumber: string, index: number) => {
    api
      .post('transfer/otherbank/namelookup', {
        destinationAccount: accountNumber,
        sourceAccount: auth.data?.accountNumber,
        institutionId: values.beneficiaryData[index].bankCode,
      }, true, true)
      .then((response) => {
        setLookingUp(false);
        if (!response.name) {
          updateLookingErrorText({ [`beneficiaryData[${index}].accountNumber`]: 'Account not found' });
        } else {
          removeKeyFromLookingErrorText(`beneficiaryData[${index}].accountNumber`);
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLookingUp(false);
        updateLookingErrorText({ [`beneficiaryData[${index}].accountNumber`]: 'Account not found' });
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 500), []);

  const handleChangeAccountNumber = (form: FormType, values: IFormValues, index: number) => ({ target }) => {
    const previousValue = form.getFieldState(target.name as 'beneficiaryData')?.initial;
    const currentValue = form.getFieldState(target.name as 'beneficiaryData')?.value;
    if (previousValue !== currentValue && target.value && values.beneficiaryData[index].transferType === 'INSTANT_INTERBANK') {
      setLookingUp(true);
      setLookingIndex(index);
      debounced(values, target.value, index);
    }
  };

  const searchBankBranch = (id: string, values: IFormValues, index: number) => {
    setLoadingBranchList(true);

    api
      .get(`/transfer/bankBranch?bankSortCode=${id}`, {}, true, true)
      .then((response: IApiBranchReponse) => {
        mainForm.change(`beneficiaryData[${index}].branchList` as any, response.banks);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingBranchList(false);
      });
  };

  const handleChangeInstantType = (index: number) => ({ target }) => {
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].bankCode`, undefined);
    mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].bankBranchCode`, undefined);
    mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].accountNumber`, '');
  };

  const handleChangeBank = (values: IFormValues, index: number) => ({ target }) => {
    const bankName = () => {
      if (values.beneficiaryData[index].transferType === 'INSTANT_INTERBANK') {
        return instantBanks.find((bank) => bank.code === target.value)?.name;
      }
      if (values.beneficiaryData[index].transferType === 'NON_INSTANT_INTERBANK') {
        return nonInstantBanks.find((bank: IBank) => bank.BankSortCode === target.value)?.BankName;
      }
      return undefined;
    };
    mainForm.change(`beneficiaryData[${index}].bankName` as any, bankName());
    mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].bankBranchCode`, undefined);
    mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].accountNumber`, '');
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    if (values.beneficiaryData[index].transferType === 'NON_INSTANT_INTERBANK') {
      searchBankBranch(target.value, values, index);
    }
  };

  const handleChangeBranch = (values: IFormValues, index: number) => ({ target }) => {
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    const selectedBranch = values.beneficiaryData[index]?.branchList.find((branch) => branch.BranchSortCode === target?.value);
    mainForm.change(`beneficiaryData[${index}].bankBranch` as any, selectedBranch?.BranchName);
  };


  const handleDeleteBen = () => {
    setShowWarning(true);
  };

  const handleAddBen = () => {
    mainForm.mutators.push('beneficiaryData', '');
  };

  const checkAccountNameFieldError = (errors, fieldName: string) => {
    if (errors[fieldName] || Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return true;
    }
    return false;
  };


  const errorAccountNameText = (errors, fieldName: string) => {
    if (Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return lookingErrorText[fieldName];
    }
    if (errors[fieldName]) {
      return errors[fieldName] as string;
    }
    return '';
  };

  const onSubmit = (values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const postBody = {
      bulkId: data.id,
      transferBeneficiaries: values.beneficiaryData.map((beneficiary) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { branchList: branch, ...ben } = { ...beneficiary };
        // if (beneficiary.isTransferTypeDisabled) {
        //   delete ben.transferType;
        // }
        delete ben.isTransferTypeDisabled;

        return ben;
      }),
    };

    console.log(postBody);

    api
      .put('/beneficiary/transfer/updateBulk', postBody, false, true)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        console.error(error.description);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        setShowStatus(true);
      });
  };

  const onSubmitWarning = () => {
    setSubmitWarningStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/transfer/deleteBulk', { bulkId: data.id })
      .then((response: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.SUCCESS);
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.FAILURE);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setShowWarning(false);
        setShowStatus(true);
      });
  };

  const closeWarning = () => {
    setShowWarning(false);
  };

  const isValidationError = Object.keys(lookingErrorText).length !== 0;

  const loadingRow = (index: number) => isLookingUp && lookingIndex === index;

  return (
    <>
      {loadingInitialBranches && <Loader fullHeight />}
      {!loadingInitialBranches && (
        <BulksWrapper onClickBack={onClose} onDeleteBen={handleDeleteBen}>
          {/* <pre>{JSON.stringify(instantBanks, null, 2)}</pre> */}
          {/* <pre>{JSON.stringify(nonInstantBanks, null, 2)}</pre> */}
          {/* <pre>{JSON.stringify(branchList, null, 2)}</pre> */}
          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            validate={validate}
            mutators={{ changeFieldValue, setFieldTouched, ...arrayMutators }}
            render={({ handleSubmit, form, values, errors, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  {/* <pre>{JSON.stringify(dataState, null, 2)}</pre> */}
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                  {/* <pre>{JSON.stringify(errors, null, 2)}</pre> */}
                  {/* <pre>{JSON.stringify(lookingErrorText, null, 2)}</pre> */}
                  {/* <pre>{JSON.stringify(lookingIndex, null, 2)}</pre> */}
                  <FieldArray name="beneficiaryData">
                    {({ fields }) => (
                      <>
                        {fields.map((name: string, index: number) => (
                          <div key={name} className={styles.bulkRow}>
                            <div className={styles.bulkFields}>
                              <Input
                                name={`${name}.fullName`}
                                label="Full Name"
                                disabled={submitting}
                                className={styles.formField}
                                // error={checkFieldError(errors, `${name}.name`)}
                                // helperText={errorText(errors, `${name}.name`)}
                              />
                              <Select
                                name={`${name}.transferType`}
                                label="Transfer Type"
                                disabled={submitting || values.beneficiaryData[index].isTransferTypeDisabled}
                                // disabled
                                className={styles.formField}
                                SelectProps={{
                                  onChange: handleChangeInstantType(index),
                                }}
                              >
                                <SelectItem key="local" value="LOCAL">
                                  Domestic
                                </SelectItem>
                                <SelectItem key="instant" value="INSTANT_INTERBANK">
                                  Instant Transfer
                                </SelectItem>
                                <SelectItem key="non-Instant" value="NON_INSTANT_INTERBANK">
                                  Non-Instant Transfer
                                </SelectItem>
                              </Select>
                              <Select
                                name={`${name}.bankCode`}
                                label="Bank"
                                disabled={submitting || loadingBranchList || values.beneficiaryData[index].transferType === 'LOCAL'}
                                className={styles.formField}
                                SelectProps={{
                                  onChange: handleChangeBank(values, index),
                                }}
                              >
                                {values.beneficiaryData[index].transferType === 'INSTANT_INTERBANK' && instantBanks.map(({ name: bankName, code }) => (
                                  <SelectItem key={bankName} value={code}>
                                    {bankName}
                                  </SelectItem>
                                ))}
                                {values.beneficiaryData[index].transferType === 'NON_INSTANT_INTERBANK' && nonInstantBanks.map(({ BankName, BankSortCode }) => (
                                  <SelectItem key={BankName} value={BankSortCode}>
                                    {BankName}
                                  </SelectItem>
                                ))}
                              </Select>
                              <Select
                                name={`${name}.bankBranchCode`}
                                label="Bank Branch"
                                disabled={
                                  submitting
                                  || loadingBranchList
                                  || values.beneficiaryData[index].transferType === 'INSTANT_INTERBANK'
                                  || values.beneficiaryData[index].transferType === 'LOCAL'
                                }
                                className={styles.formField}
                                SelectProps={{
                                  onChange: handleChangeBranch(values, index),
                                }}
                              >
                                {values.beneficiaryData[index].branchList && values.beneficiaryData[index].branchList.map(({ BranchSortCode, BranchName }) => (
                                  <SelectItem key={BranchSortCode} value={BranchSortCode}>
                                    {BranchName}
                                  </SelectItem>
                                ))}
                              </Select>
                              <AccountInput
                                name={`${name}.accountNumber`}
                                label="Account Number"
                                disabled={submitting}
                                onBlur={handleChangeAccountNumber(form, values, index)}
                                className={styles.formField}
                                error={checkAccountNameFieldError(errors, `${name}.accountNumber`)}
                                helperText={errorAccountNameText(errors, `${name}.accountNumber`)}
                              />
                            </div>
                            {loadingRow(index) && <Loader size={40} />}
                            {!loadingRow(index) && fields.length && fields.length > 1 ? (
                              <>
                                <div
                                  className={styles.removeRowBtn}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => fields.remove(index)}
                                  onKeyPress={noop}
                                >
                                  <img src={trash} alt="delete icon" width="40" height="40" />
                                </div>

                                <Button className={styles.deleteBeneficiaryMobileBtn} onClick={() => fields.remove(index)}>
                                  Delete Beneficiary
                                </Button>
                              </>
                            ) : null}
                          </div>
                        ))}
                        <button type="button" className={styles.addBeneficiaryBtn} onClick={handleAddBen}>
                          <img src={plus} alt="add icon" width="12" height="12" />
                          Add one more beneficiary
                        </button>
                        <Button
                          type="submit"
                          className={styles.submitBtn}
                          disabled={hasValidationErrors || isValidationError || !form.getState().dirty || isLookingUp || submitting}
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </FieldArray>
                  <WarningModal
                    open={showWarning}
                    submitName="Delete"
                    submitting={warningSubmitting}
                    onSubmit={onSubmitWarning}
                    onClose={closeWarning}
                  >
                    Are you sure you want to delete the Bulk Beneficiary List?
                  </WarningModal>
                  <StatusModal
                    open={showStatus}
                    status={successMessage ? 'success' : 'failure'}
                    onRetry={onCloseStatusModal}
                    onClose={onCloseStatusModal}
                    onRetryText="Continue"
                  >
                    {successMessage || errorMessage}
                  </StatusModal>
                </form>
              );
            }}
          />
        </BulksWrapper>
      )}
    </>
  );
};

export default EditTransferBulkBen;
